import {
  Badge,
  HStack,
  IconButton,
  Link,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from '@chakra-ui/react';
import {
  FaArrowRotateRight,
  FaDownload,
  FaEnvelope,
  FaFileCode,
} from 'react-icons/fa6';
import { Link as RouterLink } from 'react-router-dom';
import { ListNfseOutput } from 'services/interfaces/nfse';
import { R$ } from 'shared/helpers/currency';
import { formatDate, formatDocument } from '../helpers/csvValidation';
import { useNfseReportContext } from '../NfseReportContext';
import {
  NFSE_REPORT_STATUS_COLORS,
  NFSE_REPORT_STATUS_LABELS,
  NfseReportStatus,
  RETRANSMIT_ALLOWED_STATUS,
} from '../types/nfse.types';

interface NfseReportTableProps {
  data: ListNfseOutput[];
}

const NfseReportTable: React.FC<NfseReportTableProps> = ({ data }) => {
  const {
    retransmitNfse,
    retransmittingIds,
    downloadDanfse,
    downloadingDanfseKeys,
    downloadXml,
    downloadingXmlKeys,
    sendEmail,
    sendingEmailKeys,
  } = useNfseReportContext();
  const toast = useToast();

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: 'Copiado!',
        description: 'Chave de acesso copiada para a área de transferência',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch {
      toast({
        title: 'Erro',
        description: 'Não foi possível copiar a chave de acesso',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const canRetransmit = (status: string): boolean => {
    return RETRANSMIT_ALLOWED_STATUS.includes(status as NfseReportStatus);
  };

  const getPrice = (item: ListNfseOutput): string => {
    if (item.orderId && item.orderPrice) {
      return R$(item.orderPrice) ?? 'R$ 0,00';
    }

    if (item.batchRecordId && item.batchRecordPrice) {
      return R$(item.batchRecordPrice) ?? 'R$ 0,00';
    }

    return 'R$ 0,00';
  };

  const getStatusColor = (status: string): string => {
    return NFSE_REPORT_STATUS_COLORS[status as NfseReportStatus] || 'gray';
  };

  const getStatusLabel = (status: string): string => {
    return NFSE_REPORT_STATUS_LABELS[status as NfseReportStatus] || status;
  };

  const truncateText = (text: string | null, maxLength: number): string => {
    if (!text) return '-';
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  };

  return (
    <TableContainer>
      <Table variant="simple" size="sm">
        <Thead>
          <Tr h="3.5rem">
            <Th textAlign="center" w="60px">
              DPS ID
            </Th>
            <Th textAlign="center" w="80px">
              Cobrança ID
            </Th>
            <Th textAlign="center" w="80px">
              Valor
            </Th>
            <Th textAlign="center" w="140px">
              Documento
            </Th>
            <Th textAlign="center" w="100px">
              Status
            </Th>
            <Th textAlign="center" w="100px">
              Cód. Serviço
            </Th>
            <Th textAlign="center" w="100px">
              NBS
            </Th>
            <Th textAlign="center" w="120px">
              Chave de Acesso
            </Th>
            <Th textAlign="center" w="150px">
              Erro
            </Th>
            <Th textAlign="center" w="100px">
              Atualizado em
            </Th>
            <Th textAlign="center" w="80px">
              Ações
            </Th>
          </Tr>
        </Thead>

        <Tbody>
          {data.map((item) => (
            <Tr key={item.id} _hover={{ bg: 'gray.50' }}>
              <Td textAlign="center">{item.id}</Td>
              <Td textAlign="center">
                {item.orderId ?? item.batchRecordId ?? '-'}
              </Td>
              <Td textAlign="center">{getPrice(item)}</Td>
              <Td textAlign="center">
                {item.customerDocument ? (
                  <Link
                    as={RouterLink}
                    to={`/customer/${item.customerDocument}`}
                    target="_blank"
                    color="yooga.500"
                    fontWeight="medium"
                  >
                    {formatDocument(item.customerDocument)}
                  </Link>
                ) : (
                  '-'
                )}
              </Td>
              <Td textAlign="center">
                <Badge colorScheme={getStatusColor(item.status)}>
                  {getStatusLabel(item.status)}
                </Badge>
              </Td>
              <Td textAlign="center">{item.nationalTaxCode}</Td>
              <Td textAlign="center">{item.nbsCode}</Td>
              <Td textAlign="center">
                {item.accessKey ? (
                  <Tooltip label="Clique para copiar" hasArrow>
                    <Text
                      cursor="pointer"
                      color="yooga.500"
                      _hover={{ textDecoration: 'underline' }}
                      onClick={() => copyToClipboard(item.accessKey!)}
                    >
                      {truncateText(item.accessKey, 15)}
                    </Text>
                  </Tooltip>
                ) : (
                  '-'
                )}
              </Td>
              <Td textAlign="center">
                {item.errorCode || item.errorMessage ? (
                  <Tooltip
                    label={`${item.errorCode ?? ''}: ${item.errorMessage ?? ''}`}
                    hasArrow
                  >
                    <Text color="red.500" cursor="pointer">
                      {truncateText(item.errorMessage || item.errorCode, 25)}
                    </Text>
                  </Tooltip>
                ) : (
                  '-'
                )}
              </Td>
              <Td textAlign="center">{formatDate(item.updatedAt)}</Td>
              <Td textAlign="center">
                {(canRetransmit(item.status) && item.orderId) ||
                (item.status === 'AUTHORIZED' && item.accessKey) ? (
                  <HStack justify="center" spacing={1}>
                    {canRetransmit(item.status) && item.orderId && (
                      <Tooltip label="Retransmitir NFS-e" hasArrow>
                        <IconButton
                          aria-label="Retransmitir"
                          icon={<FaArrowRotateRight />}
                          size="sm"
                          colorScheme="yooga"
                          variant="ghost"
                          isLoading={retransmittingIds.includes(item.orderId)}
                          onClick={() => retransmitNfse(item.orderId!)}
                        />
                      </Tooltip>
                    )}
                    {item.status === 'AUTHORIZED' && item.accessKey && (
                      <>
                        <Tooltip label="Baixar DANFSE" hasArrow>
                          <IconButton
                            aria-label="Baixar DANFSE"
                            icon={<FaDownload />}
                            size="sm"
                            colorScheme="yooga"
                            variant="ghost"
                            isLoading={downloadingDanfseKeys.includes(
                              item.accessKey,
                            )}
                            onClick={() => downloadDanfse(item.accessKey!)}
                          />
                        </Tooltip>
                        <Tooltip label="Baixar XML" hasArrow>
                          <IconButton
                            aria-label="Baixar XML"
                            icon={<FaFileCode />}
                            size="sm"
                            colorScheme="yooga"
                            variant="ghost"
                            isLoading={downloadingXmlKeys.includes(
                              item.accessKey,
                            )}
                            onClick={() => downloadXml(item.accessKey!)}
                          />
                        </Tooltip>
                        <Tooltip label="Enviar E-mail" hasArrow>
                          <IconButton
                            aria-label="Enviar E-mail"
                            icon={<FaEnvelope />}
                            size="sm"
                            colorScheme="yooga"
                            variant="ghost"
                            isLoading={sendingEmailKeys.includes(
                              item.accessKey,
                            )}
                            onClick={() => sendEmail(item.accessKey!)}
                          />
                        </Tooltip>
                      </>
                    )}
                  </HStack>
                ) : (
                  '-'
                )}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default NfseReportTable;
